import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TEmAssignmentResponsePayload,
	TFetchContractDocumentRequest,
	TFetchContractDocumentResponsePayload,
	TFetchUsersResponsePayload,
	TGetContractDocumentsResponsePayload,
	TGetDownloadedDocumentsResponsePayload,
	TUpdateSettingsRequest,
	TUpdateUserRequest,
	TDeleteUserRequest,
	TUpdateNotificationSubscriptionRequest,
	TFetchNotificationSubscriptionsRequest,
	TFetchNotificationSubscriptionsResponsePayload,
	TCredentialsResetRequest,
	TSettingsStore,
	T2FAStatusResponsePayload,
	T2FAEnableRequest,
	T2FADisableRequest,
	TSpecialSchemesResponsePayload,
	TIntegrationsResponsePayload,
	TNewWixIntegrationRequest,
	TNewSquarespaceIntegrationRequest,
	TNewStripeIntegrationRequest,
	TGetHmrcOauthUrlResponsePayload,
	TSetHmrcOauthRequest,
	TUploadedSalesReportsResponsePayload,
	TUploadedSalesReportsRequest,
	TUploadSalesReportRequest,
	TUploadSalesReportResponsePayload,
	TDownloadSalesReportRequest,
	TDownloadSalesReportResponsePayload,
	TCheckHmrcAvailabilityResponsePayload,
	TGetContractDocumentsRequest,
	TDownloadContractDocumentRequest,
	TCreateDashboardUserRequest,
	TCreateDashboardUserResponsePayload,
	TSupportedCountryListResponsePayload,
	TAllCountriesResponsePayload,
} from './types';
// ==========================================:
export const initialState: TSettingsStore = {
	emAssignment: null,
	emAssignmentLoader: false,
	contractDocuments: null,
	contractDocumentsLoader: false,
	downloadedDocuments: null,
	downloadedDocumentsLoader: false,
	contractDocument: null,
	contractDocumentLoader: false,
	notificationSettings: null,
	notificationSettingsLoader: false,
	createDashboardUser: null,
	createDashboardUserLoader: false,
	createDashboardUserError: null,
	needConfirmAccessCreateDashboardUser: false,
	notificationSubscriptions: null,
	notificationSubscriptionsLoader: false,
	passwordConfirmedLoader: false,
	passwordConfirmedError: '',
	resetCredentials: false,
	resetCredentialsLoader: false,
	passwordConfirmed2FA: false,
	status2FA: null,
	status2FALoader: false,
	enable2FALoader: false,
	enable2FAError: '',
	enable2FA: false,
	disable2FALoader: false,
	disable2FAError: '',
	disable2FA: false,
	specialSchemes: null,
	specialSchemesLoader: false,
	integrations: null,
	integrationsLoader: false,
	newWixIntegrationLoader: false,
	newWixIntegrationError: null,
	newSquarespaceIntegrationLoader: false,
	newSquarespaceIntegrationError: null,
	newStripeIntegrationLoader: false,
	newStripeIntegrationError: null,
	hmrcOauthUrl: null,
	hmrcOauthUrlLoader: false,
	hmrcOauthUrlError: null,
	uploadedSalesReports: null,
	uploadedSalesReportsLoader: false,
	uploadSalesReport: null,
	uploadSalesReportLoader: false,
	downloadSalesReport: null,
	downloadSalesReportLoader: false,
	hmrcAvailability: null,
	hmrcAvailabilityLoader: false,
	supportedCountryList: null,
	supportedCountryListLoader: false,
	allCountries: null,
	allCountriesLoader: false,
};

// ==========================================:
const settings = createSlice({
	name: '@@settings',
	initialState,
	reducers: {
		getEmAssignmentRequest: (state: TSettingsStore) => {
			const getEmAssignmentState = state;
			getEmAssignmentState.emAssignmentLoader = true;
		},
		getEmAssignmentSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TEmAssignmentResponsePayload>,
		) => {
			const { payload } = action;
			const getEmAssignmentSuccessState = state;
			getEmAssignmentSuccessState.emAssignment = payload;
			getEmAssignmentSuccessState.emAssignmentLoader = false;
		},
		updateSettingsRequest: (
			state: TSettingsStore,
			action: PayloadAction<TUpdateSettingsRequest>,
		) => {},
		getContractDocumentsRequest: (
			state: TSettingsStore,
			action: PayloadAction<TGetContractDocumentsRequest>,
		) => {
			const { payload } = action;
			const getContractDocumentsState = state;
			getContractDocumentsState.contractDocumentsLoader = true;
		},
		getContractDocumentsSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TGetContractDocumentsResponsePayload>,
		) => {
			const { payload } = action;
			const getContractDocumentsSuccessState = state;
			getContractDocumentsSuccessState.contractDocuments = payload;
			getContractDocumentsSuccessState.contractDocumentsLoader = false;
		},
		fetchContractDocumentRequest: (
			state: TSettingsStore,
			action: PayloadAction<TDownloadContractDocumentRequest>,
		) => {
			const fetchContractDocumentRequestState = state;
			fetchContractDocumentRequestState.contractDocumentLoader = true;
		},
		fetchContractDocumentSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TFetchContractDocumentResponsePayload>,
		) => {
			const { payload } = action;
			const fetchContractDocumentSuccessState = state;
			fetchContractDocumentSuccessState.contractDocument = payload;
			fetchContractDocumentSuccessState.contractDocumentLoader = false;
		},
		getNotificationSettingsRequest: (state: TSettingsStore) => {
			const getNotificationSettingsState = state;
			getNotificationSettingsState.notificationSettingsLoader = true;
		},
		getNotificationSettingsSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TFetchUsersResponsePayload>,
		) => {
			const { payload } = action;
			const getNotificationSettingsSuccessState = state;
			getNotificationSettingsSuccessState.notificationSettings = payload;
			getNotificationSettingsSuccessState.notificationSettingsLoader = false;
		},
		updateUserRequest: (state: TSettingsStore, action: PayloadAction<TUpdateUserRequest>) => {},
		deleteUserRequest: (state: TSettingsStore, action: PayloadAction<TDeleteUserRequest>) => {},
		createDashboardUserRequest: (
			state: TSettingsStore,
			action: PayloadAction<TCreateDashboardUserRequest>,
		) => {
			const createDashboardUserRequestState = state;
			createDashboardUserRequestState.createDashboardUserLoader = true;
			createDashboardUserRequestState.createDashboardUserError = null;
			createDashboardUserRequestState.createDashboardUser = null;
			createDashboardUserRequestState.needConfirmAccessCreateDashboardUser = false;
		},
		createDashboardUserSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TCreateDashboardUserResponsePayload>,
		) => {
			const { payload } = action;
			const createDashboardUserSuccessState = state;
			createDashboardUserSuccessState.createDashboardUser = payload;
			createDashboardUserSuccessState.createDashboardUserLoader = false;
			createDashboardUserSuccessState.createDashboardUserError = null;
			createDashboardUserSuccessState.needConfirmAccessCreateDashboardUser = false;
		},
		createDashboardUserError: (state: TSettingsStore, action: PayloadAction<string>) => {
			const { payload } = action;
			const createDashboardUserErrorState = state;
			createDashboardUserErrorState.createDashboardUserError = payload;
			createDashboardUserErrorState.createDashboardUserLoader = false;
			createDashboardUserErrorState.createDashboardUser = null;
			createDashboardUserErrorState.needConfirmAccessCreateDashboardUser = false;
		},
		needConfirmAccessCreateDashboardUser: (
			state: TSettingsStore,
			action: PayloadAction<boolean>,
		) => {
			const { payload } = action;
			const needConfirmAccessCreateDashboardUserState = state;
			needConfirmAccessCreateDashboardUserState.needConfirmAccessCreateDashboardUser = payload;
		},
		updateNotificationSubscriptionRequest: (
			state: TSettingsStore,
			action: PayloadAction<TUpdateNotificationSubscriptionRequest>,
		) => {},
		getNotificationSubscriptionsRequest: (
			state: TSettingsStore,
			action: PayloadAction<TFetchNotificationSubscriptionsRequest>,
		) => {
			const getNotificationSubscriptionsState = state;
			getNotificationSubscriptionsState.notificationSubscriptionsLoader = true;
		},
		getNotificationSubscriptionsSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TFetchNotificationSubscriptionsResponsePayload>,
		) => {
			const { payload } = action;
			const getNotificationSubscriptionsSuccessState = state;
			getNotificationSubscriptionsSuccessState.notificationSubscriptions = payload;
			getNotificationSubscriptionsSuccessState.notificationSubscriptionsLoader = false;
		},
		resetCredentials: (state: TSettingsStore, action: PayloadAction<TCredentialsResetRequest>) => {
			const { payload } = action;
			const resetCredentialsState = state;
			resetCredentialsState.resetCredentialsLoader = true;
		},
		resetCredentialsSuccess: (state: TSettingsStore) => {
			const resetCredentialsSuccessState = state;
			resetCredentialsSuccessState.resetCredentials = true;
			resetCredentialsSuccessState.resetCredentialsLoader = false;
		},
		confirmCurrentPasswordError: (state: TSettingsStore, action: PayloadAction<string>) => {
			const { payload } = action;
			const confirmCurrentPasswordErrorState = state;
			confirmCurrentPasswordErrorState.passwordConfirmedError = payload;
			confirmCurrentPasswordErrorState.passwordConfirmedLoader = false;
		},
		get2FAStatusRequest: (state: TSettingsStore) => {
			const get2FAStatusState = state;
			get2FAStatusState.status2FALoader = true;
		},
		get2FAStatusSuccess: (
			state: TSettingsStore,
			action: PayloadAction<T2FAStatusResponsePayload>,
		) => {
			const { payload } = action;
			const get2FAStatusSuccessState = state;
			get2FAStatusSuccessState.status2FA = payload;
			get2FAStatusSuccessState.status2FALoader = false;
		},
		enable2FARequest: (state: TSettingsStore, action: PayloadAction<T2FAEnableRequest>) => {
			const enable2FARequestState = state;
			enable2FARequestState.enable2FALoader = true;
			enable2FARequestState.enable2FA = false;
		},
		enable2FASuccess: (state: TSettingsStore) => {
			const enable2FASuccessState = state;
			enable2FASuccessState.enable2FALoader = false;
			enable2FASuccessState.enable2FA = true;
		},
		enable2FAError: (state: TSettingsStore, action: PayloadAction<string>) => {
			const { payload } = action;
			const enable2FAErrorState = state;
			enable2FAErrorState.enable2FAError = payload;
			enable2FAErrorState.enable2FALoader = false;
			enable2FAErrorState.enable2FA = false;
		},
		disable2FARequest: (state: TSettingsStore, action: PayloadAction<T2FADisableRequest>) => {
			const disable2FARequestState = state;
			disable2FARequestState.disable2FALoader = true;
			disable2FARequestState.disable2FA = false;
		},
		disable2FASuccess: (state: TSettingsStore) => {
			const disable2FASuccessState = state;
			disable2FASuccessState.disable2FALoader = false;
			disable2FASuccessState.disable2FA = true;
		},
		disable2FAError: (state: TSettingsStore, action: PayloadAction<string>) => {
			const { payload } = action;
			const disable2FAErrorState = state;
			disable2FAErrorState.disable2FAError = payload;
			disable2FAErrorState.disable2FALoader = false;
			disable2FAErrorState.disable2FA = false;
		},
		getSpecialSchemesRequest: (state: TSettingsStore) => {
			const getSpecialSchemesState = state;
			getSpecialSchemesState.specialSchemesLoader = true;
		},
		getSpecialSchemesSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TSpecialSchemesResponsePayload>,
		) => {
			const { payload } = action;
			const getSpecialSchemesSuccessState = state;
			getSpecialSchemesSuccessState.specialSchemes = payload;
			getSpecialSchemesSuccessState.specialSchemesLoader = false;
		},
		getIntegrationsRequest: (state: TSettingsStore) => {
			const getIntegrationsState = state;
			getIntegrationsState.integrationsLoader = true;
		},
		getIntegrationsSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TIntegrationsResponsePayload>,
		) => {
			const { payload } = action;
			const getIntegrationsSuccessState = state;
			getIntegrationsSuccessState.integrations = payload;
			getIntegrationsSuccessState.integrationsLoader = false;
		},
		newWixIntegrationRequest: (
			state: TSettingsStore,
			action: PayloadAction<TNewWixIntegrationRequest>,
		) => {
			const newWixIntegrationRequestState = state;
			newWixIntegrationRequestState.newWixIntegrationLoader = true;
		},
		newWixIntegrationSuccess: (state: TSettingsStore) => {
			const newWixIntegrationSuccessState = state;
			newWixIntegrationSuccessState.newWixIntegrationLoader = false;
			newWixIntegrationSuccessState.newWixIntegrationError = null;
		},
		newWixIntegrationSubmitError: (state: TSettingsStore, action: PayloadAction<string>) => {
			const { payload } = action;
			const newWixIntegrationSubmitErrorState = state;
			newWixIntegrationSubmitErrorState.newWixIntegrationLoader = false;
			newWixIntegrationSubmitErrorState.newWixIntegrationError = payload;
		},
		newSquarespaceIntegrationRequest: (
			state: TSettingsStore,
			action: PayloadAction<TNewSquarespaceIntegrationRequest>,
		) => {
			const newSquarespaceIntegrationRequestState = state;
			newSquarespaceIntegrationRequestState.newSquarespaceIntegrationLoader = true;
		},
		newSquarespaceIntegrationSuccess: (state: TSettingsStore) => {
			const newSquarespaceIntegrationSuccessState = state;
			newSquarespaceIntegrationSuccessState.newSquarespaceIntegrationLoader = false;
			newSquarespaceIntegrationSuccessState.newSquarespaceIntegrationError = null;
		},
		newSquarespaceIntegrationSubmitError: (
			state: TSettingsStore,
			action: PayloadAction<string>,
		) => {
			const { payload } = action;
			const newSquarespaceIntegrationSubmitErrorState = state;
			newSquarespaceIntegrationSubmitErrorState.newSquarespaceIntegrationLoader = false;
			newSquarespaceIntegrationSubmitErrorState.newSquarespaceIntegrationError = payload;
		},
		newStripeIntegrationRequest: (
			state: TSettingsStore,
			action: PayloadAction<TNewStripeIntegrationRequest>,
		) => {
			const newStripeIntegrationRequestState = state;
			newStripeIntegrationRequestState.newStripeIntegrationLoader = true;
		},
		newStripeIntegrationSuccess: (state: TSettingsStore) => {
			const newStripeIntegrationSuccessState = state;
			newStripeIntegrationSuccessState.newStripeIntegrationLoader = false;
			newStripeIntegrationSuccessState.newStripeIntegrationError = null;
		},
		newStripeIntegrationSubmitError: (state: TSettingsStore, action: PayloadAction<string>) => {
			const { payload } = action;
			const newStripeIntegrationSubmitErrorState = state;
			newStripeIntegrationSubmitErrorState.newStripeIntegrationLoader = false;
			newStripeIntegrationSubmitErrorState.newStripeIntegrationError = payload;
		},
		getHmrcOauthUrlRequest: (state: TSettingsStore) => {
			const getHmrcOauthUrlState = state;
			getHmrcOauthUrlState.hmrcOauthUrlLoader = true;
			getHmrcOauthUrlState.hmrcOauthUrlError = null;
		},
		getHmrcOauthUrlSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TGetHmrcOauthUrlResponsePayload>,
		) => {
			const { payload } = action;
			const getHmrcOauthUrlSuccessState = state;
			getHmrcOauthUrlSuccessState.hmrcOauthUrl = payload;
			getHmrcOauthUrlSuccessState.hmrcOauthUrlLoader = false;
			getHmrcOauthUrlSuccessState.hmrcOauthUrlError = null;
		},
		setHmrcOauthError: (state: TSettingsStore, action: PayloadAction<string>) => {
			const { payload } = action;
			const setHmrcOauthErrorState = state;
			setHmrcOauthErrorState.hmrcOauthUrlLoader = false;
			setHmrcOauthErrorState.hmrcOauthUrlError = payload;
		},
		setHmrcOauthRequest: (state: TSettingsStore, action: PayloadAction<TSetHmrcOauthRequest>) => {},
		deleteHmrcOauthRequest: (state: TSettingsStore) => {},
		settingsInitState: () => initialState,
		getUploadedSalesReportsRequest: (
			state: TSettingsStore,
			action: PayloadAction<TUploadedSalesReportsRequest>,
		) => {
			const getUploadedSalesReportsState = state;
			getUploadedSalesReportsState.uploadedSalesReportsLoader = true;
		},
		getUploadedSalesReportsSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TUploadedSalesReportsResponsePayload>,
		) => {
			const { payload } = action;
			const getUploadedSalesReportsSuccessState = state;
			getUploadedSalesReportsSuccessState.uploadedSalesReports = payload;
			getUploadedSalesReportsSuccessState.uploadedSalesReportsLoader = false;
		},
		getHmrcAvailabilityRequest: (state: TSettingsStore) => {
			const s = state;
			s.hmrcAvailabilityLoader = true;
		},
		getHmrcAvailabilitySuccess: (
			state: TSettingsStore,
			action: PayloadAction<TCheckHmrcAvailabilityResponsePayload>,
		) => {
			const s = state;
			s.hmrcAvailability = action.payload;
			s.hmrcAvailabilityLoader = false;
		},
		getSupportedCountryListRequest: (state: TSettingsStore) => {
			const s = state;
			s.supportedCountryListLoader = true;
		},
		getSupportedCountryListSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TSupportedCountryListResponsePayload>,
		) => {
			const s = state;
			s.supportedCountryList = action.payload;
			s.supportedCountryListLoader = false;
		},
		getAllCountriesRequest: (state: TSettingsStore) => {
			const s = state;
			s.allCountriesLoader = true;
		},
		getAllCountriesSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TAllCountriesResponsePayload>,
		) => {
			const s = state;
			s.allCountries = action.payload;
			s.allCountriesLoader = false;
		},
		uploadSalesReportRequest: (
			state: TSettingsStore,
			action: PayloadAction<TUploadSalesReportRequest>,
		) => {
			const uploadSalesReportRequestState = state;
			uploadSalesReportRequestState.uploadSalesReportLoader = true;
		},
		uploadSalesReportSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TUploadSalesReportResponsePayload>,
		) => {
			const uploadSalesReportSuccessState = state;
			uploadSalesReportSuccessState.uploadSalesReportLoader = false;
		},
		downloadSalesReportRequest: (
			state: TSettingsStore,
			action: PayloadAction<TDownloadSalesReportRequest>,
		) => {
			const downloadSalesReportRequestState = state;
			downloadSalesReportRequestState.downloadSalesReportLoader = true;
		},
		downloadSalesReportSuccess: (
			state: TSettingsStore,
			action: PayloadAction<TDownloadSalesReportResponsePayload>,
		) => {
			const { payload } = action;
			const downloadSalesReportSuccessState = state;
			downloadSalesReportSuccessState.downloadSalesReport = payload;
			downloadSalesReportSuccessState.downloadSalesReportLoader = false;
		},
	},
});

export default settings.reducer;
export const {
	getEmAssignmentRequest,
	getEmAssignmentSuccess,
	updateSettingsRequest,
	getContractDocumentsRequest,
	getContractDocumentsSuccess,
	fetchContractDocumentRequest,
	fetchContractDocumentSuccess,
	getNotificationSettingsRequest,
	getNotificationSettingsSuccess,
	updateUserRequest,
	deleteUserRequest,
	createDashboardUserRequest,
	createDashboardUserSuccess,
	createDashboardUserError,
	needConfirmAccessCreateDashboardUser,
	updateNotificationSubscriptionRequest,
	getNotificationSubscriptionsRequest,
	getNotificationSubscriptionsSuccess,
	resetCredentials,
	resetCredentialsSuccess,
	confirmCurrentPasswordError,
	get2FAStatusRequest,
	get2FAStatusSuccess,
	enable2FARequest,
	enable2FASuccess,
	enable2FAError,
	disable2FARequest,
	disable2FASuccess,
	disable2FAError,
	getSpecialSchemesRequest,
	getSpecialSchemesSuccess,
	getIntegrationsRequest,
	getIntegrationsSuccess,
	newWixIntegrationRequest,
	newWixIntegrationSuccess,
	newWixIntegrationSubmitError,
	newSquarespaceIntegrationRequest,
	newSquarespaceIntegrationSuccess,
	newSquarespaceIntegrationSubmitError,
	newStripeIntegrationRequest,
	newStripeIntegrationSuccess,
	newStripeIntegrationSubmitError,
	getHmrcOauthUrlRequest,
	getHmrcOauthUrlSuccess,
	setHmrcOauthError,
	setHmrcOauthRequest,
	deleteHmrcOauthRequest,
	settingsInitState,
	getUploadedSalesReportsRequest,
	getUploadedSalesReportsSuccess,
	uploadSalesReportRequest,
	uploadSalesReportSuccess,
	downloadSalesReportRequest,
	downloadSalesReportSuccess,
	getHmrcAvailabilityRequest,
	getHmrcAvailabilitySuccess,
	getSupportedCountryListRequest,
	getSupportedCountryListSuccess,
	getAllCountriesRequest,
	getAllCountriesSuccess,
} = settings.actions;
